/**
 * Camera QR scanner for the voter slip: the phone's own BarcodeDetector when it has one (Chrome on
 * Android), else jsQR on video frames (iPhone Safari and others; loaded only when needed). The camera
 * stops as soon as the scanner closes. The same code is reported at most once every 3 seconds.
 */
import { useEffect, useRef, useState } from "react";
import { freshScan } from "../field/voted";
import type { Strings } from "../i18n";
import { Button } from "./components";

type Detect = (v: HTMLVideoElement) => Promise<string | null>;
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
interface BarcodeDetectorCtor {
  new (o: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

async function makeDetector(): Promise<Detect> {
  const BD = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  if (BD) {
    const ok = await (BD.getSupportedFormats?.() ?? Promise.resolve(["qr_code"])).then((f) => f.includes("qr_code")).catch(() => false);
    if (ok) {
      const d = new BD({ formats: ["qr_code"] });
      return async (v) => (await d.detect(v))[0]?.rawValue ?? null;
    }
  }
  const { default: jsQR } = await import("jsqr");
  const canvas = document.createElement("canvas");
  const g = canvas.getContext("2d", { willReadFrequently: true });
  return async (v) => {
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!g || !w || !h) return null;
    const k = Math.min(1, 720 / Math.max(w, h));
    canvas.width = Math.round(w * k);
    canvas.height = Math.round(h * k);
    g.drawImage(v, 0, 0, canvas.width, canvas.height);
    const img = g.getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" })?.data ?? null;
  };
}

export function QrScanner({ t, onCode, onClose }: { t: Strings; onCode: (text: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const cb = useRef(onCode);
  cb.current = onCode;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer = 0;
    let last: { text: string; at: number } | null = null;
    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErr(t.vtCameraUnsupported);
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      } catch {
        setErr(t.vtCameraNo);
        return;
      }
      if (stopped || !video.current) {
        stream.getTracks().forEach((x) => x.stop());
        return;
      }
      const v = video.current;
      v.srcObject = stream;
      v.setAttribute("playsinline", "true");
      v.muted = true;
      await v.play().catch(() => undefined);
      let detect: Detect;
      try {
        detect = await makeDetector();
      } catch {
        setErr(t.vtCameraUnsupported);
        return;
      }
      const tick = async () => {
        if (stopped) return;
        try {
          const text = await detect(v);
          const now = Date.now();
          if (text && freshScan(last, text, now)) {
            last = { text, at: now };
            cb.current(text);
          }
        } catch {
          /* a frame that could not be read: try the next */
        }
        if (!stopped) timer = window.setTimeout(() => void tick(), 180);
      };
      void tick();
    })();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((x) => x.stop());
    };
  }, [t]);

  return (
    <div className="scanner" data-testid="qr-scanner">
      {err ? (
        <div className="banner banner-warn" role="alert">
          {err}
        </div>
      ) : (
        <>
          <div className="scanner-view">
            <video ref={video} playsInline muted aria-label={t.vtScanHint} />
            <span className="scanner-frame" aria-hidden />
          </div>
          <p className="muted" style={{ textAlign: "center", margin: "6px 0 10px" }}>
            {t.vtScanHint}
          </p>
        </>
      )}
      <Button variant="secondary" onClick={onClose} testId="scan-close">
        {t.vtScanClose}
      </Button>
    </div>
  );
}
